import { supabase } from "@/lib/supabase";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { Recipe } from "@/features/Recipes-renamed/types";
import { mapDatabaseRecipeToRecipe, type RecipesRowWithRelations } from "@/lib/mapDatabaseRecipeToRecipe";
import { useAuthContext } from "@/features/authentication/useAuthContext";

const MS_PER_DAY = 1000 * 60 * 60 * 24;

export function useUntriedRecipes(count: number): UseQueryResult<Recipe[]> {
    const { user } = useAuthContext();
    return useQuery({
        enabled: !!user,
        queryKey: ["untriedRecipes", count],
        queryFn: async () => {
            const { data, error } = await supabase
                .from("recipes")
                .select(
                    `
                        *,
                        recipe_types (*),
                        recipe_categories (*),
                        ingredient_groups (*, ingredients (*))
                    `
                )
                .eq("user_id", user!.id)
                .eq("has_made_it", false);

            if (error || !data) {
                console.error("Error fetching untried recipes:", error);
                throw error;
            }

            // Weighted random sample biased toward older recipes (Efraimidis-Spirakis):
            // each row gets a sort key = random()^(1/weight); higher weight ⇒ larger
            // expected key. Age in days (floored at 1) is the weight, so something
            // sitting in the library for months out-weights last week's add but
            // doesn't deterministically win.
            const now = Date.now();
            const ranked = data.map((row) => {
                const created = row.created_at ? new Date(row.created_at).getTime() : now;
                const ageDays = Math.max(1, (now - created) / MS_PER_DAY);
                const sortKey = Math.pow(Math.random(), 1 / ageDays);
                return { row, sortKey };
            });

            ranked.sort((a, b) => b.sortKey - a.sortKey);

            return ranked
                .slice(0, count)
                .map(({ row }) => mapDatabaseRecipeToRecipe(row as unknown as RecipesRowWithRelations));
        }
    });
}
